import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { Box, Card, CardActionArea, CardContent, CardHeader, Grid, Typography } from "@mui/material";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import AccomplishmentSkill from "./AccomplishmentSkill";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import RenderDate from "../../../Common/Date/RenderDate";
import TMSAvatar from "../TMSAvatar";

function AccomplishmentCard({ accomplishment, downloadAction }) {
    const title = accomplishment?.description?.length > 0 ? accomplishment.description : "Accomplishment";
    const dateAcquired = !!accomplishment?.dateAcquired
        ? <RenderDate date={accomplishment.dateAcquired} />
        : "Not specified";
    const handleDownload = useCallback(
        (onSuccess) => downloadAction(accomplishment.id, onSuccess),
        [accomplishment.id, downloadAction]
    );

    const type = !!accomplishment?.skill?.name ? "skill" : accomplishment;

    let badge, alertSeverity, alertMessage;
    const dateExpires = accomplishment?.dateExpired || null;
    const expired = !!dateExpires && dayjs().isAfter(dateExpires, "day");
    const healthy = !!dateExpires && dayjs().diff(dateExpires, "month", true) < -1;

    if (!dateExpires) {
        badge = 'checked';
        alertSeverity = 'success';
        alertMessage = 'Never expires';
    } else if (expired) {
        badge = 'error';
        alertSeverity = 'error';
        alertMessage = `Expired ${dayjs().to(dayjs(dateExpires))}`;
    } else if (!healthy) {
        badge = 'info';
        alertSeverity = 'warning';
        alertMessage = `Expires ${dayjs().to(dayjs(dateExpires))}`;
    } else {
        badge = 'info';
        alertSeverity = 'success';
        alertMessage = `Expires ${dayjs().to(dayjs(dateExpires))}`;
    }

    let header = (
        <CardHeader
            disableTypography={true}
            avatar={<TMSAvatar type={type} badge={badge} />}
            title={
                <Typography component="h2" className="card-header-title">
                    {title}
                </Typography>
            }
            subheader={
                <Typography component="span" className="card-header-subtitle" >
                    {dateAcquired}
                </Typography>
            }
        />
    );

    if (!!accomplishment?.skill?.certificate) {
        header = (
            <CardActionArea component={Link} to={`/tms/accomplishment/${accomplishment.id}`} centerRipple >
                {header}
            </CardActionArea>
        );
    }


    return (
        <Card className="card">
            {header}
            <CardContent>
                <Box className="card-content">
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={12} md={6}>
                            <Typography variant="body2">
                                <strong>Acquired</strong>&nbsp;{dateAcquired}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Expires</strong>&nbsp;
                                {accomplishment.dateExpired ? <RenderDate date={accomplishment.dateExpired} /> : "never"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            {accomplishment.grade && (
                                <Typography variant="body2">
                                    <strong>Grade</strong>{" "}
                                    {accomplishment.grade}
                                </Typography>
                            )}
                            {accomplishment.score && (
                                <Typography variant="body2">
                                    <strong>Score</strong>{" "}
                                    {accomplishment.score}
                                </Typography>
                            )}
                            {accomplishment.cpdCredits && (
                                <Typography variant="body2">
                                    <strong>CPD</strong>{" "}
                                    {accomplishment.cpdCredits}
                                </Typography>
                            )}
                        </Grid>
                        {accomplishment.notes && (
                            <Grid item xs={12} sm={12} md={12}>
                                <Typography variant="body2">
                                    <strong>Notes</strong>{" "}
                                    {accomplishment.notes}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </CardContent>

            <CardContent>
                <Box className="card-content">
                    <AccomplishmentSkill
                        skill={accomplishment.skill}
                        onDownload={handleDownload}
                    />
                </Box>
            </CardContent>

            <CardContent>
                <Box className="card-content">
                    <AlertMessage
                        severity={alertSeverity}
                        message={alertMessage}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}

AccomplishmentCard.propTypes = {
    accomplishment: PropTypes.shape({
        id: PropTypes.number.isRequired,
        description: PropTypes.string,
        dateAcquired: PropTypes.oneOfType([
            PropTypes.instanceOf(Date),
            PropTypes.string,
        ]),
        dateExpired: PropTypes.oneOfType([
            PropTypes.instanceOf(Date),
            PropTypes.string,
        ]),
        grade: PropTypes.string,
        score: PropTypes.string,
        cpdCredits: PropTypes.number,
        notes: PropTypes.string,
        skill: PropTypes.shape({
            name: PropTypes.string.isRequired,
        }),
    }),

    downloadAction: PropTypes.func,
};
export default AccomplishmentCard;
